import { formatShareCode, type Plan } from "@roxpacer/core";
import { useEffect, useState } from "react";
import { signIn, useAuth } from "../auth";
import { sharePath } from "../lib/router";
import { flushSync, shareApi } from "../sync";
import type { ShareCode } from "../sync/api";

type State =
  | { status: "loading" }
  | { status: "not-shared" }
  | { status: "shared"; share: ShareCode }
  | { status: "error"; message: string };

function CopyButton({ text, label }: { text: string; label: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className="btn"
      aria-label={label}
      onClick={() =>
        void navigator.clipboard.writeText(text).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        })
      }
    >
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

function ShareCodeDetails({ planId }: { planId: string }) {
  const [state, setState] = useState<{ planId: string; value: State } | null>(null);
  const [busy, setBusy] = useState(false);
  const current: State = state?.planId === planId ? state.value : { status: "loading" };
  const set = (value: State) => setState({ planId, value });
  const fail = (error: unknown) =>
    set({
      status: "error",
      message: error instanceof Error ? error.message : "Something went wrong",
    });

  useEffect(() => {
    let cancelled = false;
    shareApi.get(planId).then(
      (share) =>
        !cancelled &&
        setState({ planId, value: share ? { status: "shared", share } : { status: "not-shared" } }),
      (error: unknown) =>
        !cancelled &&
        setState({
          planId,
          value: {
            status: "error",
            message: error instanceof Error ? error.message : "Something went wrong",
          },
        }),
    );
    return () => {
      cancelled = true;
    };
  }, [planId]);

  const run = async (action: () => Promise<State>) => {
    setBusy(true);
    try {
      set(await action());
    } catch (error) {
      fail(error);
    } finally {
      setBusy(false);
    }
  };
  const createCode = () =>
    run(async () => {
      // The plan must be saved to the account before it can be shared.
      await flushSync();
      return { status: "shared", share: await shareApi.create(planId) };
    });
  const stopSharing = () =>
    run(async () => {
      await shareApi.revoke(planId);
      return { status: "not-shared" };
    });

  switch (current.status) {
    case "loading":
      return <p className="text-sm text-muted">Loading…</p>;
    case "error":
      return (
        <p role="alert" className="text-sm text-danger">
          {current.message}
        </p>
      );
    case "not-shared":
      return (
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-sm text-ink-2">
            Create a share code to send this plan to teammates or load it on a watch. Anyone with
            the code can view the plan, including later changes.
          </p>
          <button type="button" className="btn btn-primary" disabled={busy} onClick={createCode}>
            Create share code
          </button>
        </div>
      );
    case "shared": {
      const { code, expiresAt } = current.share;
      const link = `${window.location.origin}${sharePath(code)}`;
      return (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
            <div>
              <p className="label">Share code</p>
              <div className="flex items-center gap-2">
                <span
                  className="font-mono text-2xl font-semibold tracking-wider"
                  data-testid="share-code"
                >
                  {formatShareCode(code)}
                </span>
                <CopyButton text={formatShareCode(code)} label="Copy share code" />
              </div>
            </div>
            <div className="min-w-0 flex-1">
              <p className="label">Link</p>
              <div className="flex items-center gap-2">
                <a
                  href={link}
                  className="truncate text-sm underline"
                  target="_blank"
                  rel="noreferrer"
                >
                  {link}
                </a>
                <CopyButton text={link} label="Copy share link" />
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <p className="mr-auto text-xs text-muted">
              Anyone with the code can view this plan, including later changes. Expires{" "}
              {new Date(expiresAt).toLocaleDateString(undefined, { dateStyle: "medium" })}.
            </p>
            <button type="button" className="btn" disabled={busy} onClick={createCode}>
              New code
            </button>
            <button type="button" className="btn text-danger" disabled={busy} onClick={stopSharing}>
              Stop sharing
            </button>
          </div>
        </div>
      );
    }
  }
}

export function SharePanel({ plan }: { plan: Plan }) {
  const status = useAuth((s) => s.status);

  return (
    <section className="card p-4" aria-label="Share plan">
      {status === "signed-in" ? (
        <ShareCodeDetails planId={plan.id} />
      ) : (
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-sm text-ink-2">
            Sign in to share this plan with teammates or load it on a watch.
          </p>
          {status !== "unavailable" && (
            <button type="button" className="btn btn-primary" onClick={() => void signIn()}>
              Sign in
            </button>
          )}
        </div>
      )}
    </section>
  );
}
